/**
 * Дзвінок з картки `call` (calls.md, розд. 2, 5): ключі полів, які читає веб, і чисті функції над
 * значеннями. Без рантайму `@clientline/contracts` і без React: файл спільний для рядка дзвінка
 * (картка) і панелі результату (лінивий чанк).
 */
import type { AppMeta, MetaEntity, RecordCard } from '@clientline/contracts';

/** Поведінка модуля `calls` (calls.md, розд. 2): за нею веб знаходить сутність дзвінка в `/meta`. */
export const CALLS_BEHAVIOR = 'calls';

/**
 * Сутність дзвінка — за поведінкою `calls` у `/meta`, а не за ключем (рішення Архітектора 22.10):
 * немає сутності з цією поведінкою (модуль не встановлено чи роль її не бачить) — `undefined`.
 */
export function callEntityOf(meta: AppMeta | undefined): MetaEntity | undefined {
  return meta?.entities.find((e) => CALLS_BEHAVIOR in (e.behaviors ?? {}));
}

/** Поля результату дзвінка — поведінки `calls`, основа `call_` (behaviors.md, розд. 4). */
export const CALL_OUTCOME_FIELDS = {
  outcome: 'call_outcome',
  reason: 'call_outcome_reason',
  note: 'call_outcome_note',
  /** Коли записано результат: порожнє — «без результату». */
  at: 'call_outcome_at',
  activityType: 'call_activity_type',
} as const;

/** Стартовий тип активності дзвінка платформи (без результатів). */
export const CALL_DEFAULT_TYPE = 'call';

const text = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/** Задача-план дзвінка; поле приховане чи порожнє — `null`. */
export function callPlanOf(call: RecordCard): string | null {
  return text(call.values.plan);
}

/**
 * Тип результатів (розд. 5): дзвінок із задачі — тип задачі-плану, інакше `call_activity_type`,
 * без нього — стартовий `call`. Картки дзвінка ще немає (блок до `id`, тікет 512e18c4) — так само,
 * як сервер ставить тип новому дзвінку: хук `beforeCreate` модуля дає `call`, а операція `outcome`
 * бере тип плану (`modules/calls/server/hooks.ts`, `operations/outcome.ts`).
 */
export function callTypeOf(call: RecordCard | null, plan: RecordCard | null): string {
  return (
    text(plan?.values.type) ??
    text(call?.values[CALL_OUTCOME_FIELDS.activityType]) ??
    CALL_DEFAULT_TYPE
  );
}

export type CallPhase = 'initiated' | 'ringing' | 'talking' | 'ended';

export function callPhaseOf(call: RecordCard): CallPhase {
  const phase = call.values.phase;
  return phase === 'ringing' || phase === 'talking' || phase === 'ended' ? phase : 'initiated';
}

/** Результат уже записано (`call_outcome_at` є). */
export function callHasOutcome(call: RecordCard): boolean {
  return call.values[CALL_OUTCOME_FIELDS.at] != null;
}

/**
 * Записати дзвінок з картки можна (fail closed): сутність `call` є в `/meta` і людина може її
 * створювати. Інакше «Подзвонити» лише відкриває набір (`tel:`) і копіює номер.
 */
export function canRecordCalls(entity: MetaEntity | undefined): boolean {
  return entity?.canCreate === true;
}

/** Секунди між двома мітками ISO; немає однієї — `null`. */
export function secondsBetween(from: unknown, to: unknown): number | null {
  if (typeof from !== 'string' || typeof to !== 'string') return null;
  const s = Math.round((Date.parse(to) - Date.parse(from)) / 1000);
  return Number.isFinite(s) && s >= 0 ? s : null;
}

/** Тривалість «4:12» чи «1:02:03» (табличні цифри, без мовних слів). */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}
